import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Users, Plus, Trash2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import InitialsAvatar from '../components/InitialsAvatar';
import SenderDetails from '../components/settings/SenderDetails';

const InviteDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('manager');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setName(''); setEmail(''); setRole('manager'); setError(null); } }, [open]);

  const submit = async () => {
    if (!name.trim() || !email.trim()) { setError('Name and email are required'); return; }
    setSaving(true); setError(null);
    try {
      const { data } = await api.post('/users', { name: name.trim(), email: email.trim(), role });
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to invite user');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>invite team member</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">they'll get access when they sign in with this Gmail.</p>
        <div className="space-y-3 mt-3">
          <div><Label>Full name</Label><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Jamie Reid" className="mt-1" /></div>
          <div><Label>Gmail address</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jamie@gmail.com" className="mt-1" /></div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">admin — full access</SelectItem>
                <SelectItem value="talent_manager">talent admin — full access except clients, expenses & finances</SelectItem>
                <SelectItem value="social_manager">social manager — social, mailers, projects, database, insights, to-do, calendar & expenses</SelectItem>
                <SelectItem value="talent">talent — sees only their own data</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={saving} onClick={submit} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">{saving ? 'sending…' : 'send invite'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Settings = () => {
  const { user, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [rowError, setRowError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users');
      setUsers(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { role });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
      if (u.user_id === user?.user_id) refresh();
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update role');
    }
  };

  const toggleActive = async (u) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { active: !u.active });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update user');
    }
  };

  const updateTitle = async (u, title) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { title });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
      if (u.user_id === user?.user_id) refresh();
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update title');
    }
  };

  const toggleEditingRights = async (u) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { editing_rights: !u.editing_rights });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update editing rights');
    }
  };

  const toggleHours = async (u) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { can_log_hours: !u.can_log_hours });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update hours access');
    }
  };

  const toggleSocialAccess = async (u) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { social_access: !u.social_access });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
      if (u.user_id === user?.user_id) refresh();
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update social access');
    }
  };

  const updateHourlyRate = async (u, rate) => {
    setRowError(null);
    try {
      const { data } = await api.patch(`/users/${u.user_id}`, { hourly_rate: rate === '' || rate == null ? null : Number(rate) });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? data : x)));
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to update hourly rate');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? Their access will be revoked immediately.`)) return;
    setRowError(null);
    try {
      await api.delete(`/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      setRowError(e?.response?.data?.detail || 'Failed to remove user');
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">settings</h1>
          <p className="text-sm text-slate-500 mt-1">manage who has access to your workspace and what they can see.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> invite team member
        </Button>
      </header>

      <div className="mb-6">
        <SenderDetails />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">team</h3>
          <span className="text-xs text-slate-400">({users.length})</span>
        </div>

        {rowError && <div className="mx-5 mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{rowError}</div>}

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-5 py-3">person</th>
                <th className="text-left font-medium px-4 py-3">role</th>
                <th className="text-center font-medium px-4 py-3">social</th>
                <th className="text-left font-medium px-4 py-3">status</th>
                <th className="text-center font-medium px-4 py-3">hours</th>
                <th className="text-center font-medium px-4 py-3">editing rights</th>
                <th className="text-left font-medium px-4 py-3">£ / hour</th>
                <th className="text-left font-medium px-4 py-3">joined</th>
                <th className="w-40"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.user_id === user?.user_id;
                return (
                  <tr key={u.user_id} className="border-t border-slate-100">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <InitialsAvatar name={u.name} size={36} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-slate-800 truncate">{u.name}</div>
                            {isMe && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">you</span>}
                          </div>
                          <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</div>
                          <div className="mt-1">
                            <Input
                              defaultValue={u.title || ''}
                              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (u.title || '')) updateTitle(u, v); }}
                              placeholder="add a title (e.g. Founder, Head of Talent)"
                              className="h-6 text-[11px] px-2 max-w-[260px] border-0 bg-transparent hover:bg-slate-50 focus:bg-white focus:border-slate-200"
                              data-testid={`user-title-input-${u.user_id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="h-8 w-40 text-xs bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manager">admin</SelectItem>
                          <SelectItem value="talent_manager">talent admin</SelectItem>
                          <SelectItem value="social_manager">social manager</SelectItem>
                          <SelectItem value="talent">talent</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.role === 'manager' || u.role === 'talent_manager' || u.role === 'social_manager' ? (
                        <span className="text-[11px] text-slate-400" title="this role already includes social access">✓</span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={Boolean(u.social_access)}
                          onChange={() => toggleSocialAccess(u)}
                          className="w-4 h-4 rounded border-slate-300 text-pink-500 focus:ring-pink-400"
                          title="also give this talent social manager access (social, mailers, projects, database, insights)"
                          data-testid={`user-social-access-toggle-${u.user_id}`}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleActive(u)} disabled={isMe} className={`text-xs px-2 py-1 rounded-full ${u.active ? 'bg-mint-50 text-mint-700 hover:bg-mint-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} disabled:opacity-50`}>
                        {u.active ? 'active' : 'inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={Boolean(u.can_log_hours)}
                        onChange={() => toggleHours(u)}
                        className="w-4 h-4 rounded border-slate-300 text-pink-500 focus:ring-pink-400"
                        title="toggle hours access"
                        data-testid={`user-hours-toggle-${u.user_id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.role === 'talent' ? (
                        <input
                          type="checkbox"
                          checked={Boolean(u.editing_rights)}
                          onChange={() => toggleEditingRights(u)}
                          className="w-4 h-4 rounded border-slate-300 text-pink-500 focus:ring-pink-400"
                          title="let this talent edit a limited set of fields on their own deals & pitches (notes, contact, content status, drive link, deliverables, dates, caption, PO, rate & stage)"
                          data-testid={`user-editing-rights-toggle-${u.user_id}`}
                        />
                      ) : (
                        <span className="text-[11px] text-slate-300" title="only applies to talent accounts">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        defaultValue={u.hourly_rate ?? ''}
                        placeholder="—"
                        disabled={!u.can_log_hours}
                        onBlur={(e) => { const v = e.target.value; if ((v === '' ? null : Number(v)) !== (u.hourly_rate ?? null)) updateHourlyRate(u, v); }}
                        className="h-8 w-24 text-xs border border-slate-200 rounded px-2 outline-none focus:ring-2 focus:ring-pink-200 disabled:opacity-50"
                        data-testid={`user-hourly-rate-${u.user_id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {!isMe && (
                        <button onClick={() => remove(u)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 inline-flex items-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={9} className="text-center text-slate-400 text-sm py-8">no team members yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-slate-400">
        managers can invite others, edit deals, brands, invoicing and pitches. talent role is read‑only and scoped to their own data. tick <span className="font-medium">social</span> to also give a talent the social manager areas (social, mailers, projects, database, insights). deactivating a user blocks new sign‑ins but keeps their data.
      </p>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onCreated={(u) => setUsers((prev) => [u, ...prev])} />
    </div>
  );
};

export default Settings;
